import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { reaction } from "mobx";

import { chatStoreInstance } from "@/components/VibeChat/hooks/useVibeChat";
import { StreamEventType, ResourceType, RESOURCE_TYPES } from "@/components/VibeChat/types";
import type {
  StreamResource,
  ComputerAgentResults,
  NavigationItem,
} from "@/components/VibeChat/stores/types";
import {
  RESOURCE_LABELS,
  RESOURCE_ENTITY_TYPE,
} from "@/components/VibeChat/utils/resourceDetection";
import useActiveWorkspace from "@/hooks/useActiveWorkspace";
import { routePaths } from "@/utils/routePaths";

interface ComputerAgentProgressEventData {
  type: StreamEventType.COMPUTER_AGENT_PROGRESS;
  content?: string;
  human_readable_paragraph?: string;
  resources?: StreamResource[];
}

interface ComputerAgentCompleteEventData {
  type: StreamEventType.COMPUTER_AGENT_COMPLETE;
  results?: ComputerAgentResults;
}

interface ToolResultEventData {
  type: StreamEventType.TOOL_RESULT;
  navigation?: NavigationItem[];
}

type FollowModeEventData =
  | ComputerAgentProgressEventData
  | ComputerAgentCompleteEventData
  | ToolResultEventData;

/**
 * This attribute is used to identify the area of the chat that is in follow mode.
 * It is used to detect clicks outside the chat area to automatically disable follow mode.
 */
const FOLLOW_MODE_AREA_ATTR_KEY = "data-follow-mode-area";
const DATA_ATTR_PROP_FOLLOW_MODE_AREA = { [FOLLOW_MODE_AREA_ATTR_KEY]: true };

const RESOURCE_URL_BUILDERS: Record<ResourceType, (wid: number, id: number) => string> = {
  [RESOURCE_TYPES.PROMPT]: routePaths.workspacePrompt,
  [RESOURCE_TYPES.DATASET]: routePaths.workspaceDataset,
  [RESOURCE_TYPES.BLUEPRINT]: routePaths.workspaceBlueprint,
  [RESOURCE_TYPES.FOLDER]: routePaths.workspaceFolder,
  [RESOURCE_TYPES.WORKFLOW]: routePaths.workspaceWorkflow,
};

const getFirstInternalNavigationUrl = (navigation?: NavigationItem[]) =>
  navigation?.find(item => item.url.startsWith("/"))?.url;

export const useFollowMode = () => {
  const navigate = useNavigate();
  const workspace = useActiveWorkspace();
  const queryClient = useQueryClient();

  // Click-outside detection to auto-disable follow mode
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (!chatStoreInstance.isFollowMode) {
      return;
    }

    const target = e.target as HTMLElement;
    if (target.closest(`[${FOLLOW_MODE_AREA_ATTR_KEY}]`) || target.closest('[role="dialog"]')) {
      return;
    }

    chatStoreInstance.setFollowMode(false);
  }, []);

  /**
   * --------- EFFECTS ---------
   *
   * - Click-outside detection to auto-disable follow mode
   * - Auto on/off follow mode based on streaming state
   * - Listen to stream events for resource actions
   */

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // Auto on/off follow mode based on streaming state
  useEffect(() => {
    const dispose = reaction(
      () => chatStoreInstance.isLoading,
      (isLoading, wasLoading) => {
        if (isLoading && !wasLoading) {
          // Auto-enable for fresh chats (user msg + empty assistant = 2 messages)
          if (chatStoreInstance.messages.length === 2) {
            chatStoreInstance.setFollowMode(true);
          }
        } else if (!isLoading && wasLoading) {
          // Streaming finished — auto-disable
          chatStoreInstance.setFollowMode(false);
        }
      },
    );
    return dispose;
  }, []);

  // Listen to stream events for resource actions
  useEffect(() => {
    if (!workspace?.id) {
      return;
    }

    const workspaceId =
      typeof workspace.id === "string" ? parseInt(workspace.id, 10) : workspace.id;

    const unsubscribe = chatStoreInstance.addEventListener((eventType, data) => {
      const followEvent = data as unknown as FollowModeEventData;

      if (!chatStoreInstance.isFollowMode) {
        return;
      }

      switch (followEvent.type) {
        case StreamEventType.COMPUTER_AGENT_PROGRESS:
          for (const resource of followEvent.resources ?? []) {
            const urlBuilder = RESOURCE_URL_BUILDERS[resource.type as ResourceType];
            if (!urlBuilder) {
              continue;
            }

            navigate(urlBuilder(workspaceId, resource.id));
            if (resource.status === "updated") {
              queryClient.invalidateQueries({ queryKey: ["folder-entities"] });
              queryClient.invalidateQueries({ queryKey: [resource.type, resource.id] });
            }
          }
          break;

        case StreamEventType.COMPUTER_AGENT_COMPLETE:
          for (const resource of followEvent.results?.resources ?? []) {
            const urlBuilder = RESOURCE_URL_BUILDERS[resource.type as ResourceType];
            if (!urlBuilder) {
              continue;
            }

            navigate(urlBuilder(workspaceId, resource.id));
          }
          break;

        case StreamEventType.TOOL_RESULT: {
          const url = getFirstInternalNavigationUrl(followEvent.navigation);
          if (url) {
            navigate(url);
          }
          break;
        }

        default:
          break;
      }
    });

    return unsubscribe;
  }, [workspace?.id, navigate, queryClient]);
};

export {
  FOLLOW_MODE_AREA_ATTR_KEY,
  DATA_ATTR_PROP_FOLLOW_MODE_AREA,
  RESOURCE_LABELS,
  RESOURCE_ENTITY_TYPE,
};
